/**
 * Pure helpers for student Sessions tab (Phase A redesign).
 */

import { isContentActive } from "./contentIsActiveHelpers";

export function sessionDisplayTitle(session, idx = 0) {
  const n = session?.sessionNumber != null ? session.sessionNumber : idx + 1;
  const name =
    session?.name ||
    session?.title ||
    session?.topic ||
    null;
  if (name) return `Session ${n} – ${name}`;
  return `Session ${n}`;
}

/** Parse topics from newline string, JSON array string, or string[]. */
export function parseSessionTopics(topics) {
  if (topics == null || topics === "") return [];
  if (Array.isArray(topics)) {
    return topics.map((t) => String(t).trim()).filter(Boolean);
  }
  const raw = String(topics).trim();
  if (!raw) return [];
  if (raw.startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((t) => String(t).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  return raw
    .split(/\r?\n|;/)
    .map((t) => t.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
}

/**
 * Collect session materials for expand panel.
 * docs unlocked when session status is today|completed (not upcoming).
 */
export function materialsForSession({
  sessionId,
  sessionDocs = {},
  documents = [],
  checklists = [],
  tests = [],
  status,
  forceUnlock = false,
}) {
  const sid = String(sessionId);
  const unlocked =
    forceUnlock || status === "today" || status === "completed";

  // Date-unlock is already decided above via `status`; delegate through
  // isContentActive (with no sessionsById) so it applies just the enabled
  // check, same rule used everywhere else content is counted.
  const isActive = (item) => isContentActive(item);

  const fromSessionDocs = sessionDocs[sessionId] || sessionDocs[sid] || [];
  const fromClassDocs = (documents || []).filter(
    (d) => d.sessionId != null && String(d.sessionId) === sid,
  );
  // de-dupe by id
  const seen = new Set();
  const docs = [...fromSessionDocs, ...fromClassDocs].filter((d) => {
    const id = d.id ?? `${d.contentType}-${d.fileName || d.url}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  }).filter(isActive);

  const skills = (checklists || [])
    .filter((c) => c.sessionId != null && String(c.sessionId) === sid)
    .filter(isActive);
  const sessionTests = (tests || [])
    .filter((t) => t.sessionId != null && String(t.sessionId) === sid)
    .filter(isActive);

  return {
    unlocked,
    docs,
    checklists: skills,
    tests: sessionTests,
    isEmpty: docs.length === 0 && skills.length === 0 && sessionTests.length === 0,
  };
}

export function sessionStatusBadge(status) {
  if (status === "today") {
    return {
      label: "IN PROGRESS",
      color: "var(--color-accent-pink)",
      bg: "color-mix(in srgb, var(--color-accent-pink) 15%, transparent)",
      border: "color-mix(in srgb, var(--color-accent-pink) 25%, transparent)",
    };
  }
  if (status === "upcoming") {
    return {
      label: "SCHEDULED",
      color: "rgba(31,22,51,0.55)",
      bg: "var(--color-surface-press-stronger)",
      border: "var(--color-hairline-cloud)",
    };
  }
  return {
    label: "ATTENDED",
    color: "var(--color-success)",
    bg: "var(--color-success-bg)",
    border: "color-mix(in srgb, var(--color-success) 25%, transparent)",
  };
}

/** Format "09:00" / "09:00:00" → "9:00 AM" */
export function formatSessionClock(timeStr) {
  if (!timeStr) return null;
  const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(timeStr);
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

export function formatSessionTimeRange(session) {
  const start = formatSessionClock(session?.startTime);
  const end = formatSessionClock(session?.endTime);
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  return "Time TBD";
}
